import { type HistoricalPrice, type CollectibleId } from "@/services/collectibles";
import { CategoryId } from "@/services/collection/categories";
import { type CollectorId } from "@/services/collectors";
import { type ApiResponseImageNoId, type ApiResponseImage } from "@/services/images";
import { type PriceNumeric, type Price, type PriceNumericOrEmpty } from "@/services/prices";

export type CollectionItemId = number;

export type CollectionItem = {
  id: CollectionItemId;

  acquirePrice: PriceNumericOrEmpty;
  currentPrice: PriceNumericOrEmpty;
  lastPrice: PriceNumericOrEmpty;
  publicOfferedPrice: Price;

  categoryId: CategoryId;
  dateAcquired: string;
  images: ApiResponseImage[];
  name: string;
  parameters: CollectionItemParameter[];

  note: string | null;
  publicNote: string;

  pieces: number;
  publicPieces: number;

  userCollectionList: string[];
  whereStored: string;
  acquisitionSource: string;
  status: ItemStatus;

  likesCount: number;
  likedByCurrentUser: boolean;

  // @todo Would be nice to get rid off this property - needs BE rework
  detail?: CollectionItemDetail;
};

export type CollectionItemWithDetail = CollectionItem & {
  detail: CollectionItemDetail;
};
export type CollectionItemWithDetail_LegoSet = CollectionItem & {
  categoryId: CategoryId.LegoSets;
  detail: CollectionItemDetail_LegoSet;
};

export type CollectionItemDetail = CollectionItemDetail_Collectible | CollectionItemDetail_LegoSet;

/** In long term we want to drop this specific detail. */
export type CollectionItemDetail_LegoSet = CollectionItemDetail_Collectible & {
  theme: { name: string };
  number: string;
  partsCount: string;
  year: string;
  dimensions: string;
  weight: string;
};

export type CollectionItemDetail_Collectible = {
  id: CollectibleId;
  name: string;
  prices: HistoricalPrice[];
  images: ApiResponseImage[];
  parameters: {
    option: {
      value: string;
    };
    parameter: {
      id: number;
      label: string;
      isWidgetIdentificator?: true;
    };
  }[];
};

// @todo revision needed, what is really used?? Should the type be even here?
export type CollectionItemParameter = {
  isCustom: 0 | 1;
  parameterId: number;
  value: string;
  valueId: number;
};

export type CreateCollectionItemData = {
  categoryId: CategoryId;
  userCollectionListName?: string;
} & (
  | {
      collectibleId: CollectibleId;
      // temp properties, will be removed after full lego migration into UU
      set: CollectibleId;
      minifigure: CollectibleId;
    }
  | {
      customName: string;
    }
);

export type ApiData_UpdateCollectionItem = Partial<{
  acquirePrice: PriceNumericOrEmpty;
  dateAcquired: string;
  currentPrice: Price;
  customName: string;
  note: string;
  pieces: number;
  publicNote: string;
  publicOfferedPrice: PriceNumericOrEmpty;
  publicPieces: number;
  where_stored: string;
  acquisition_source: string;
  aiIdentificationSourceId: string;
}>;

export enum ItemStatus {
  InMyCollection = "inCollection",
  OnMarketplace = "onMarketplace",
  Archived = "archived",
}

export type CollectionItem_ListItem = {
  id: CollectionItemId;
  name: string;
  categoryId: CategoryId;
  acquirePrice: PriceNumericOrEmpty;
  publicOfferedPrice: Price;
  currentPrice: PriceNumericOrEmpty;
  images: ApiResponseImage[];
  isOwnershipTransferRequested: boolean;
  likesCount: number;
  status: ItemStatus;
  userCollectionList: string[];
};

export enum Api_ItemPatchOperation {
  Archivate = "archivate",
  RequestOwnershipTransfer = "transferOwnership",
}

export type PublicCollectionItem_ListItem = {
  id: CollectionItemId;
  categoryId: CategoryId;
  name: string;
  images: ApiResponseImageNoId[];
  publicPrice: Price;
  publicPriceBeforeConversion: Price;
  publicPieces: number;
  publicNote: string;
  publishedDate: string; // expected YYYY-MM-DD
  marketplaceListedDate: string; // expected YYYY-MM-DD
  collectorId: CollectorId;
  collectorNickname: string;
  status: ItemStatus;
  likesCount: number;
  likedByCurrentUser?: boolean; // not available for not logged in user
};

export type PublicCollectionItem = {
  id: CollectionItemId;
  categoryId: CategoryId;
  name: string;
  collectorNickname: string;
  publishedDate: string; // expected YYYY-MM-DD
  marketplaceListedDate?: string; // expected YYYY-MM-DD
  publicPieces: number;
  publicNote: string;
  catalogueItemParams: {
    label: string;
    value: string | number;
  }[];
  collectorId: CollectorId;
  collectorItemsCount: number;
  collectorRating: number;
  collectorRatingsCount: number;
  collectorAvatar: string | null;
  publicPrice: Price;
  publicPriceBeforeConversion: Price;
  status: ItemStatus;
  images: ApiResponseImageNoId[];
  likesCount: number;
  likedByCurrentUser: boolean;
};

export type PublicCollectionItem_ForSale = PublicCollectionItem & {
  publicPrice: PriceNumeric;
};

export class PublicItemNotFoundException extends Error {}
